import { useI18n } from '@/hooks/useI18n';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Search, SlidersHorizontal, Users, X } from 'lucide-react';
import { TextReveal } from '@/components/primitives/TextReveal';
import { cn } from '@/lib/cn';
import { formatInt } from '@/lib/format';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Empty } from '@/components/primitives/Empty';
import { Stagger } from '@/components/primitives/Stagger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { useDebounce } from '@/hooks/useDebounce';
import { PAGINATION } from '@/lib/constants';
import type { AccountResponse } from '@/types/api';
import { AccountRow } from './AccountRow';
import { AccountsRowSkeleton } from './AccountsRowSkeleton';
import { CreateAccountDialog } from './CreateAccountDialog';
import { useAuth } from '@/contexts/AuthContext';
import { canManageWorkspace } from '@/lib/permissions';

type Status = 'all' | 'active' | 'inactive' | 'paused' | 'error';

const STATUS_OPTIONS: Array<{ value: Status; label: string; labelEn: string }> = [
  { value: 'all', label: 'Tous les statuts', labelEn: 'All statuses' },
  { value: 'active', label: 'Actifs', labelEn: 'Active' },
  { value: 'inactive', label: 'Inactifs', labelEn: 'Inactive' },
  { value: 'paused', label: 'En pause', labelEn: 'Paused' },
  { value: 'error', label: 'En erreur', labelEn: 'With errors' },
];

function filterLocal(list: AccountResponse[], q: string, status: Status, ids: string[]) {
  const query = q.toLowerCase().trim();
  return list.filter((a) => {
    if (query && !a.username.toLowerCase().includes(query)) return false;
    if (ids.length > 0 && !ids.includes(a.identityId ?? '')) return false;
    if (status === 'active' && !a.isActive) return false;
    if (status === 'inactive' && a.isActive) return false;
    if (status === 'paused' && !a.pausedReason) return false;
    if (status === 'error' && a.consecutiveErrors === 0) return false;
    return true;
  });
}

export function AccountsScreen() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = canManageWorkspace(user?.role);
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '0');
  const size = Number(params.get('size') ?? String(PAGINATION.defaultSize));
  const status = (params.get('status') ?? 'all') as Status;
  const searchParam = params.get('q') ?? '';
  const identitiesParam = params.get('identities') ?? '';
  const selectedIds = useMemo(() => identitiesParam.split(',').filter(Boolean), [identitiesParam]);

  const [searchInput, setSearchInput] = useState(searchParam);
  const debounced = useDebounce(searchInput, 250);

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };
  const setParam = (key: string, value: string | null) => updateParams({ [key]: value });

  const accountsQ = useAccounts({ page, size, sort: 'username,asc' });
  const identitiesQ = useIdentities();

  const filtered = useMemo(() => {
    if (!accountsQ.data) return [];
    return filterLocal(accountsQ.data.content, debounced, status, selectedIds);
  }, [accountsQ.data, debounced, status, selectedIds]);

  const pagination = accountsQ.data;
  const hasPrev = (pagination?.number ?? 0) > 0;
  const hasNext = pagination ? !pagination.last : false;
  const totalLabel = pagination
    ? t(
        `${pagination.totalElements} au total · ${filtered.length} visibles`,
        `${pagination.totalElements} total · ${filtered.length} visible`,
      )
    : undefined;

  return (
    <Stagger className="flex flex-col gap-6">
      <header className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <TextReveal className="min-w-0">
          <p className="t-stagger-line mb-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-violet-deep">
            {t('Votre bibliothèque', 'Your library')}
          </p>
          <div className="t-stagger-line">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-[28px] leading-tight text-text-1 sm:text-[30px]">
                {t('Comptes', 'Accounts')}
              </h1>
              {pagination && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-violet/15 bg-violet-soft px-2.5 py-1 text-[11px] font-medium text-violet-deep">
                  <Users className="size-3" aria-hidden="true" />
                  {formatInt(pagination.totalElements)}
                </span>
              )}
            </div>
          </div>
          <p className="t-stagger-line t-stagger-line--2 mt-2 text-[12px] leading-relaxed text-text-3 sm:text-[13px]">
            {t(
              'Retrouvez vos comptes Instagram et leur état de collecte.',
              'Find your Instagram accounts and their collection status.',
            )}
          </p>
        </TextReveal>
        {canManage && (
          <CreateAccountDialog>
            <Button variant="primary" size="lg" className="w-full gap-2 sm:w-auto sm:shrink-0">
              <Plus className="size-4" />
              {t('Ajouter un compte', 'Add an account')}
            </Button>
          </CreateAccountDialog>
        )}
      </header>

      <section
        aria-label={t('Répertoire des comptes', 'Account directory')}
        className="min-w-0 overflow-hidden rounded-2xl border border-line-1 bg-bg-1 shadow-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-1 px-4 py-4 sm:px-5">
          <h2 className="text-[14px] font-semibold text-text-1">
            {t('Comptes suivis', 'Tracked accounts')}
          </h2>
          {totalLabel && (
            <p className="text-[11px] text-text-3" aria-live="polite">
              {totalLabel}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-4 sm:px-5">
          <div className="relative w-full min-w-0 sm:min-w-[220px] sm:flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-3" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                updateParams({ q: e.target.value || null, page: '0' });
              }}
              aria-label={t('Rechercher un compte', 'Search accounts')}
              placeholder={t('Chercher @username…', 'Search @username…')}
              className="h-11 bg-bg-0 pl-8 pr-11 sm:h-11"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setParam('q', null);
                }}
                className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-text-3 hover:text-text-1 sm:h-9 sm:w-9"
                aria-label={t('Effacer', 'Clear')}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <IdentityFilter
            identities={identitiesQ.data}
            selected={selectedIds}
            onChange={(ids) => {
              updateParams({ identities: ids.join(','), page: '0' });
            }}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-1 px-4 pb-4 sm:px-5">
          <div
            className="flex min-w-0 flex-wrap items-center gap-1.5"
            role="group"
            aria-label={t('Statut des comptes', 'Account status')}
          >
            <SlidersHorizontal
              className="mr-1 hidden size-3.5 text-text-3 sm:block"
              aria-hidden="true"
            />
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={status === option.value}
                onClick={() =>
                  updateParams({ status: option.value === 'all' ? null : option.value, page: '0' })
                }
                className={cn(
                  'min-h-10 rounded-lg border px-3 text-[11px] font-medium transition-colors',
                  status === option.value
                    ? 'border-violet/20 bg-violet-soft text-violet-deep'
                    : 'border-transparent text-text-3 hover:bg-bg-2 hover:text-text-1',
                )}
              >
                {option.value === 'all' ? t('Tous', 'All') : t(option.label, option.labelEn)}
              </button>
            ))}
          </div>
          {searchInput || selectedIds.length || status !== 'all' ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                updateParams({ q: null, identities: null, status: null, page: null });
              }}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 text-[11px] font-medium text-text-3 hover:bg-bg-2 hover:text-text-1"
            >
              <X className="size-3" aria-hidden="true" />
              {t('Réinitialiser', 'Reset filters')}
            </button>
          ) : (
            <span className="text-[10px] text-text-3">
              {t('Filtres sur la page affichée', 'Filters apply to the displayed page')}
            </span>
          )}
        </div>
        <div
          aria-hidden="true"
          className="hidden grid-cols-[minmax(0,1fr)_120px_130px_44px] gap-3 border-b border-line-1 bg-bg-0/75 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-text-3 md:grid"
        >
          <span>{t('Compte et identité', 'Account and identity')}</span>
          <span>{t('Statut', 'Status')}</span>
          <span>{t('Dernière collecte', 'Last collection')}</span>
          <span className="text-right">{t('Action', 'Action')}</span>
        </div>
        {accountsQ.error ? (
          <div className="p-4">
            <ErrorInline error={accountsQ.error} onRetry={() => accountsQ.refetch()} />
          </div>
        ) : !accountsQ.isLoading && filtered.length === 0 ? (
          <div className="p-4">
            <Empty
              title={t('Aucun compte', 'No accounts')}
              description={
                debounced || selectedIds.length > 0 || status !== 'all'
                  ? t(
                      'Aucun compte ne correspond à vos filtres.',
                      'No accounts match your filters.',
                    )
                  : t(
                      'Ajoutez votre premier compte pour commencer le tracking.',
                      'Add your first account to start tracking.',
                    )
              }
            />
          </div>
        ) : (
          <div className="divide-y divide-line-1/70">
            {accountsQ.isLoading &&
              Array.from({ length: 8 }).map((_, i) => <AccountsRowSkeleton key={i} />)}
            {!accountsQ.isLoading &&
              filtered.map((a) => (
                <AccountRow
                  key={a.id}
                  account={a}
                  identity={identitiesQ.data?.find((i) => i.id === a.identityId) ?? null}
                />
              ))}
          </div>
        )}
      </section>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] text-text-3">
            <span>{t('par page', 'per page')}</span>
            <Select
              value={String(size)}
              onValueChange={(v) => {
                updateParams({ size: v, page: '0' });
              }}
            >
              <SelectTrigger
                aria-label={t('Comptes par page', 'Accounts per page')}
                className="h-11 w-[72px] text-[11px] sm:h-7"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGINATION.pageSizeOptions.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-text-3">
            <span>
              page {pagination.number + 1} / {pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasPrev}
              aria-label={t('Page précédente', 'Previous page')}
              onClick={() => setParam('page', String(page - 1))}
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasNext}
              aria-label={t('Page suivante', 'Next page')}
              onClick={() => setParam('page', String(page + 1))}
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </Stagger>
  );
}

function IdentityFilter({
  identities,
  selected,
  onChange,
}: {
  identities: { id: string; name: string; color: string | null }[] | undefined;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useI18n();
  const label =
    selected.length === 0
      ? t('Toutes identités', 'All identities')
      : selected.length === 1
        ? (identities?.find((i) => i.id === selected[0])?.name ?? t('1 identité', '1 identity'))
        : t(`${selected.length} identités`, `${selected.length} identities`);

  const toggle = (id: string) => {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  return (
    <Select
      value={selected[0] ?? 'all'}
      onValueChange={(v) => {
        if (v === 'all') onChange([]);
        else toggle(v);
      }}
    >
      <SelectTrigger
        aria-label={t('Filtrer par identité', 'Filter by identity')}
        className="h-11 w-full min-w-0 text-[12px] sm:h-11 sm:w-[210px]"
      >
        <SelectValue placeholder={t('Identité', 'Identity')}>{label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('Toutes identités', 'All identities')}</SelectItem>
        {identities?.map((i) => (
          <SelectItem key={i.id} value={i.id}>
            <span className="flex items-center gap-2">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: i.color ?? 'hsl(var(--text-3))' }}
              />
              {i.name}
              {selected.includes(i.id) && <span className="ml-auto text-violet-deep">✓</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
